// Modelo de faixa (range) da timeline, parametrizado por visão (Dia/Semana/Mês).
// Gantt contínuo: blocos e colunas usam o MESMO mapa linear (ms → px) sobre [startMs, endMs), então
// sempre se alinham (robusto a DST). Funções puras — cobertas em production-timeline-geometry.test.ts.

export type TimelineView = "day" | "week" | "month";

// Gutter da swimlane (rótulo da linha) — px reais fora do CSS bundle, via CSS var.
export const TL_GUTTER_PX = 168;
// Largura MÍNIMA de coluna por visão (px) = piso de legibilidade. A grade ocupa 100% da largura
// (área de dados em flex-1, colunas/blocos posicionados em %); só rola na horizontal quando o
// container é mais estreito que `minDataWidthPx` (= mínimo × nº de colunas), ex.: Mês em tela pequena.
const COL_MIN_PX: Record<TimelineView, number> = { day: 48, week: 110, month: 34 };

export interface TimelineCol {
  key: string;
  label: string;
  sublabel?: string;
  offsetPct: number; // % do início da coluna na área de dados (gantt contínuo, esticável)
  widthPct: number;
  major: boolean; // gridline mais forte (cada 6h no dia; segunda-feira em semana/mês)
  weekend: boolean; // sombreado leve (sáb/dom) em semana/mês
}

export interface TimelineRange {
  view: TimelineView;
  anchorIso: string;
  startMs: number;
  endMs: number; // exclusivo (início do próximo período)
  minDataWidthPx: number; // piso de largura da área de dados; abaixo disso o track rola
  cols: TimelineCol[];
}

// Clamp compartilhado pela geometria do gantt (timeline + mini-gantt do modal). Fonte única.
export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

// Timezone do navegador do operador. A timeline de OP inteira opera em hora local
// (datetime-field, eixos, blocos), então o "expediente" também é avaliado no fuso local do operador.
export const OPERATOR_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

// "yyyy-mm-dd" + "T00:00:00" (sem Z) = meia-noite LOCAL (em fuso negativo, a string pura cairia no
// dia anterior). Toda a aritmética da timeline é local p/ casar com o calendário do operador.
// Exportado p/ o filtro da lista (ProductionOrderFiltersBar) montar bounds no dia LOCAL do operador.
export function localMidnight(dayIso: string): Date {
  return new Date(`${dayIso}T00:00:00`);
}

// Fim exclusivo do dia LOCAL (= meia-noite local do dia seguinte). Par de localMidnight p/ bounds.
export function localDayEnd(dayIso: string): Date {
  const d = localMidnight(dayIso);
  d.setDate(d.getDate() + 1);
  return d;
}

// "yyyy-mm-dd" a partir dos componentes LOCAIS de um Date (não UTC — `toISOString().slice` cairia no
// dia errado em fuso negativo). Exportado p/ o mini-gantt do modal derivar o dia local da janela.
export function localIsoDay(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function todayIso(): string {
  return localIsoDay(new Date());
}

// Início do período conforme a visão: dia → meia-noite; semana → segunda-feira (ISO); mês → dia 1.
function periodStart(view: TimelineView, anchorIso: string): Date {
  const d = localMidnight(anchorIso);
  if (view === "week") {
    const dow = d.getDay(); // 0=dom..6=sáb
    d.setDate(d.getDate() - ((dow + 6) % 7)); // recua até segunda
  } else if (view === "month") {
    d.setDate(1);
  }
  return d;
}

// Passos de coluna (início de cada coluna) + fim exclusivo do período.
function colStarts(view: TimelineView, start: Date): { steps: Date[]; end: Date } {
  const steps: Date[] = [];
  if (view === "day") {
    for (let h = 0; h < 24; h++) {
      const d = new Date(start);
      d.setHours(h, 0, 0, 0);
      steps.push(d);
    }
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { steps, end };
  }
  const span = view === "week" ? 7 : daysInMonth(start);
  for (let i = 0; i < span; i++) {
    const d = new Date(start);
    d.setDate(d.getDate() + i);
    steps.push(d);
  }
  const end = new Date(start);
  end.setDate(end.getDate() + span);
  return { steps, end };
}

function daysInMonth(start: Date): number {
  return new Date(start.getFullYear(), start.getMonth() + 1, 0).getDate();
}

function shortWeekday(d: Date): string {
  return d.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
}

function colMeta(
  view: TimelineView,
  cs: Date
): Pick<TimelineCol, "label" | "sublabel" | "major" | "weekend"> {
  const dow = cs.getDay();
  const weekend = dow === 0 || dow === 6;
  if (view === "day") {
    const h = cs.getHours();
    return { label: `${String(h).padStart(2, "0")}:00`, major: h % 6 === 0, weekend: false };
  }
  if (view === "week") {
    const dm = cs.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
    return { label: shortWeekday(cs), sublabel: dm, major: dow === 1, weekend };
  }
  return { label: String(cs.getDate()), sublabel: shortWeekday(cs), major: dow === 1, weekend };
}

export function buildRange(view: TimelineView, anchorIso: string): TimelineRange {
  const start = periodStart(view, anchorIso);
  const { steps, end } = colStarts(view, start);
  const startMs = start.getTime();
  const endMs = end.getTime();
  const span = endMs - startMs;
  const pctAt = (ms: number) => ((ms - startMs) / span) * 100;
  const cols: TimelineCol[] = steps.map((cs, i) => {
    const offsetPct = pctAt(cs.getTime());
    const nextMs = (steps[i + 1] ?? end).getTime();
    return {
      key: localIsoDay(cs) + cs.getHours(),
      offsetPct,
      widthPct: pctAt(nextMs) - offsetPct,
      ...colMeta(view, cs),
    };
  });
  return { view, anchorIso, startMs, endMs, minDataWidthPx: COL_MIN_PX[view] * steps.length, cols };
}

// left/width em % (da área de dados) de um bloco, RECORTADO à faixa visível: uma OP multi-dia aparece
// em todo período que cobre (recorte aos limites). null quando não há interseção real com a faixa.
export function blockRangeGeometryPct(
  startIso: string,
  endIso: string,
  range: TimelineRange
): { leftPct: number; widthPct: number } | null {
  const span = range.endMs - range.startMs;
  const s = clamp(new Date(startIso).getTime(), range.startMs, range.endMs);
  const e = clamp(new Date(endIso).getTime(), range.startMs, range.endMs);
  if (e <= s) return null;
  return {
    leftPct: ((s - range.startMs) / span) * 100,
    widthPct: ((e - s) / span) * 100,
  };
}

// Offset em % do marcador AGORA; null quando `now` cai fora da faixa exibida.
export function rangeNowPct(now: Date, range: TimelineRange): number | null {
  const t = now.getTime();
  if (t < range.startMs || t >= range.endMs) return null;
  return ((t - range.startMs) / (range.endMs - range.startMs)) * 100;
}

// Navegação: avança/recua 1 dia/semana/mês mantendo ISO yyyy-mm-dd local.
export function shiftAnchor(view: TimelineView, anchorIso: string, dir: number): string {
  const d = localMidnight(anchorIso);
  if (view === "day") d.setDate(d.getDate() + dir);
  else if (view === "week") d.setDate(d.getDate() + dir * 7);
  else {
    d.setDate(1);
    d.setMonth(d.getMonth() + dir);
  }
  return localIsoDay(d);
}

// Bounds em ISO p/ o fetch (overlap=true): `to` = fim inclusivo da faixa (último ms antes do próximo).
export function rangeBoundsIso(range: TimelineRange): { from: string; to: string } {
  return {
    from: new Date(range.startMs).toISOString(),
    to: new Date(range.endMs - 1).toISOString(),
  };
}

export function periodLabel(range: TimelineRange): string {
  const start = new Date(range.startMs);
  if (range.view === "day") {
    return start.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" });
  }
  if (range.view === "week") {
    const end = new Date(range.endMs - 1);
    const opts = { day: "2-digit", month: "short" } as const;
    return `${start.toLocaleDateString("pt-BR", opts)} – ${end.toLocaleDateString("pt-BR", opts)}`;
  }
  return start.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
}
